import React, { useState, useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { notificationsAPI } from "../../../services/api";
import { useTheme } from "@mui/material/styles";
import {
  Box,
  Toolbar,
  IconButton,
  Typography,
  Badge,
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Tooltip,
  Switch,
  FormControlLabel,
} from "@mui/material";
import {
  Menu as MenuIcon,
  NotificationsOutlined,
  AccountCircleOutlined,
  SettingsOutlined,
  LogoutOutlined,
  Brightness4Outlined,
  Brightness7Outlined,
} from "@mui/icons-material";
import { selectUser, logoutUser } from "../../../store/slices/authSlice";
import { useTheme as useCustomTheme } from "../../ui/theme/ThemeProvider";
import { ROUTES } from "../../../constants";
import SessionCounter from "../SessionCounter";

const Header = ({ onMenuClick, drawerWidth }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const user = useSelector(selectUser);
  const { isDarkMode, toggleTheme } = useCustomTheme();

  const [anchorEl, setAnchorEl] = useState(null);
  const [notificationAnchor, setNotificationAnchor] = useState(null);
  const [notifications, setNotifications] = useState([]);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await notificationsAPI.getAll();
      const data = res.data?.data?.notifications || res.data?.notifications || [];
      setNotifications(data);
    } catch {
      // silently fail
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 60000); // poll every 60s
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const unreadCount = notifications.filter((n) => n.status !== 'read').length;

  const handleMarkRead = async (id) => {
    try {
      await notificationsAPI.markRead(id);
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, status: 'read' } : n))
      );
    } catch {
      // silently fail
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationsAPI.markAllRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, status: 'read' })));
    } catch {
      // silently fail
    }
  };

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationMenuOpen = (event) => {
    setNotificationAnchor(event.currentTarget);
  };

  const handleNotificationMenuClose = () => {
    setNotificationAnchor(null);
  };

  const handleLogout = async () => {
    handleProfileMenuClose();
    await dispatch(logoutUser());
  };

  const handleProfileClick = () => {
    handleProfileMenuClose();
    navigate(ROUTES.PROFILE);
  };

  const handleSettingsClick = () => {
    handleProfileMenuClose();
    navigate(ROUTES.SETTINGS);
  };

  const getUserDisplayName = () => {
    if (!user) return "User";

    const firstName = user.first_name || "";
    const surname = user.sur_name || "";

    if (firstName && surname) {
      return `${firstName} ${surname}`;
    }

    return user.email || "User";
  };

  const getUserInitials = () => {
    if (!user) return "U";

    const firstName = user.first_name || "";
    const surname = user.sur_name || "";

    if (firstName && surname) {
      return `${firstName.charAt(0)}${surname.charAt(0)}`.toUpperCase();
    }

    if (user.email) {
      return user.email.charAt(0).toUpperCase();
    }

    return "U";
  };

  const isMenuOpen = Boolean(anchorEl);
  const isNotificationOpen = Boolean(notificationAnchor);

  return (
    <Toolbar
      sx={{
        minHeight: { xs: 64, sm: 72 },
        px: { xs: 2, sm: 3 },
        borderBottom: `1px solid ${theme.palette.divider}`,
        bgcolor: theme.palette.mode === 'dark' 
          ? theme.palette.background.paper 
          : "rgba(255,255,255,0.8)",
        backdropFilter: theme.palette.mode === 'dark' ? "none" : "blur(20px)",
      }}
    >
      {/* Menu Toggle Button */}
      <IconButton
        aria-label="toggle drawer"
        edge="start"
        onClick={onMenuClick}
        sx={{
          mr: 2,
          bgcolor: theme.palette.mode === 'dark' 
            ? theme.palette.action.hover 
            : "#f5f5f7",
          width: 40,
          height: 40,
          color: theme.palette.text.primary,
          "&:hover": {
            bgcolor: theme.palette.mode === 'dark'
              ? theme.palette.action.selected
              : "#e8e8ed",
            transform: "scale(1.05)",
          },
          transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        <MenuIcon sx={{ fontSize: 20 }} />
      </IconButton>

      {/* App Title - Hidden on mobile when drawer is open */}
      <Typography
        variant="h6"
        noWrap
        component="div"
        sx={{
          flexGrow: 1,
          display: { xs: "none", sm: "block" },
          fontWeight: 600,
          fontSize: 18,
          color: theme.palette.text.primary,
          letterSpacing: "-0.02em",
        }}
      >
        GHF Office System
      </Typography>

      <Box sx={{ flexGrow: 1 }} />

      {/* Right side actions */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {/* Session Counter */}
        <SessionCounter />

        {/* Theme Toggle */}
        <Tooltip title={`Switch to ${isDarkMode ? "light" : "dark"} mode`}>
          <IconButton
            onClick={toggleTheme}
            sx={{
              width: 40,
              height: 40,
              bgcolor: theme.palette.mode === 'dark' 
                ? theme.palette.action.hover 
                : "#f5f5f7",
              color: theme.palette.text.secondary,
              "&:hover": {
                bgcolor: theme.palette.mode === 'dark'
                  ? theme.palette.action.selected
                  : "#e8e8ed",
                color: theme.palette.primary.main,
                transform: "scale(1.05)",
              },
              transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
            }}
          >
            {isDarkMode ? (
              <Brightness7Outlined sx={{ fontSize: 20 }} />
            ) : (
              <Brightness4Outlined sx={{ fontSize: 20 }} />
            )}
          </IconButton>
        </Tooltip>

        {/* Notifications */}
        <Tooltip title="Notifications">
          <IconButton
            onClick={handleNotificationMenuOpen}
            aria-label="show notifications"
            sx={{
              width: 40,
              height: 40,
              bgcolor: theme.palette.mode === 'dark' 
                ? theme.palette.action.hover 
                : "#f5f5f7",
              color: theme.palette.text.secondary,
              "&:hover": {
                bgcolor: theme.palette.mode === 'dark'
                  ? theme.palette.action.selected
                  : "#e8e8ed",
                color: theme.palette.primary.main,
                transform: "scale(1.05)",
              },
              transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
            }}
          >
            <Badge
              badgeContent={unreadCount || null}
              sx={{
                "& .MuiBadge-badge": {
                  bgcolor: "#ff3b30",
                  color: "white",
                  fontSize: 10,
                  height: 16,
                  minWidth: 16,
                  fontWeight: 600,
                },
              }}
            >
              <NotificationsOutlined sx={{ fontSize: 20 }} />
            </Badge>
          </IconButton>
        </Tooltip>

        {/* User Profile */}
        <Tooltip title="Account">
          <IconButton
            onClick={handleProfileMenuOpen}
            aria-label="account of current user"
            aria-controls="primary-search-account-menu"
            aria-haspopup="true"
            sx={{
              ml: 1,
              p: 0,
              "&:hover": {
                transform: "scale(1.05)",
              },
              transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
            }}
          >
            <Avatar
              sx={{
                width: 36,
                height: 36,
                bgcolor: theme.palette.primary.main,
                fontSize: 14,
                fontWeight: 600,
                boxShadow: `0 2px 8px ${theme.palette.mode === 'dark' ? 'rgba(144,202,249,0.3)' : 'rgba(80,118,171,0.2)'}`,
              }}
            >
              {getUserInitials()}
            </Avatar>
          </IconButton>
        </Tooltip>
      </Box>

      {/* Profile Menu */}
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        open={isMenuOpen}
        onClose={handleProfileMenuClose}
        PaperProps={{
          elevation: 0,
          sx: {
            mt: 1.5,
            minWidth: 240,
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: theme.palette.mode === 'dark' 
              ? "0 8px 24px rgba(0,0,0,0.4)"
              : "0 8px 24px rgba(0,0,0,0.12)",
            overflow: "visible",
            "&::before": {
              content: '""',
              display: "block",
              position: "absolute",
              top: 0,
              right: 20,
              width: 10,
              height: 10,
              bgcolor: theme.palette.background.paper,
              transform: "translateY(-50%) rotate(45deg)",
              zIndex: 0,
              borderLeft: `1px solid ${theme.palette.divider}`,
              borderTop: `1px solid ${theme.palette.divider}`,
            },
          },
        }}
      >
        {/* User Info */}
        <Box sx={{ px: 2.5, py: 2 }}>
          <Typography
            variant="subtitle1"
            noWrap
            sx={{ fontWeight: 600, color: theme.palette.text.primary }}
          >
            {getUserDisplayName()}
          </Typography>
          <Typography
            variant="body2"
            noWrap
            sx={{ color: theme.palette.text.secondary, fontSize: 13, mt: 0.5 }}
          >
            {user?.email}
          </Typography>
        </Box>

        <Divider sx={{ borderColor: theme.palette.divider }} />

        {/* Menu Items */}
        <MenuItem
          onClick={handleProfileClick}
          sx={{
            mx: 1,
            my: 0.5,
            borderRadius: 1.5,
            py: 1.5,
            "&:hover": {
              bgcolor: theme.palette.mode === 'dark'
                ? theme.palette.action.hover
                : "rgba(80,118,171,0.08)",
            },
            transition: "all 0.2s",
          }}
        >
          <ListItemIcon sx={{ color: theme.palette.primary.main, minWidth: 36 }}>
            <AccountCircleOutlined sx={{ fontSize: 20 }} />
          </ListItemIcon>
          <ListItemText
            primary="My Profile"
            primaryTypographyProps={{
              fontSize: 14,
              fontWeight: 500,
            }}
          />
        </MenuItem>

        <MenuItem
          onClick={handleSettingsClick}
          sx={{
            mx: 1,
            my: 0.5,
            borderRadius: 1.5,
            py: 1.5,
            "&:hover": {
              bgcolor: theme.palette.mode === 'dark'
                ? theme.palette.action.hover
                : "rgba(80,118,171,0.08)",
            },
            transition: "all 0.2s",
          }}
        >
          <ListItemIcon sx={{ color: theme.palette.primary.main, minWidth: 36 }}>
            <SettingsOutlined sx={{ fontSize: 20 }} />
          </ListItemIcon>
          <ListItemText
            primary="Settings"
            primaryTypographyProps={{
              fontSize: 14,
              fontWeight: 500,
            }}
          />
        </MenuItem>

        <Divider sx={{ my: 1, borderColor: "rgba(0,0,0,0.06)" }} />

        {/* Theme Toggle in Menu */}
        <MenuItem
          sx={{
            mx: 1,
            my: 0.5,
            borderRadius: 1.5,
            py: 1.5,
          }}
        >
          <FormControlLabel
            control={
              <Switch
                checked={isDarkMode}
                onChange={toggleTheme}
                size="small"
            sx={{
              "& .MuiSwitch-switchBase.Mui-checked": {
                color: theme.palette.primary.main,
              },
              "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                bgcolor: theme.palette.primary.main,
              },
            }}
              />
            }
            label="Dark Mode"
            sx={{
              ml: 0,
              "& .MuiFormControlLabel-label": {
                fontSize: 14,
                fontWeight: 500,
                color: theme.palette.text.primary,
              },
            }}
          />
        </MenuItem>

        <Divider sx={{ my: 1, borderColor: "rgba(0,0,0,0.06)" }} />

        <MenuItem
          onClick={handleLogout}
          sx={{
            mx: 1,
            my: 0.5,
            mb: 1,
            borderRadius: 1.5,
            py: 1.5,
            color: theme.palette.error.main,
            "&:hover": {
              bgcolor: theme.palette.mode === 'dark'
                ? theme.palette.error.dark + '20'
                : "rgba(255,59,48,0.08)",
            },
            transition: "all 0.2s",
          }}
        >
          <ListItemIcon sx={{ color: theme.palette.error.main, minWidth: 36 }}>
            <LogoutOutlined sx={{ fontSize: 20 }} />
          </ListItemIcon>
          <ListItemText
            primary="Sign Out"
            primaryTypographyProps={{
              fontSize: 14,
              fontWeight: 500,
            }}
          />
        </MenuItem>
      </Menu>

      {/* Notifications Menu */}
      <Menu
        anchorEl={notificationAnchor}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        open={isNotificationOpen}
        onClose={handleNotificationMenuClose}
        PaperProps={{
          elevation: 0,
          sx: {
            mt: 1.5,
            minWidth: 340,
            maxHeight: 480,
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: theme.palette.mode === 'dark' 
              ? "0 8px 24px rgba(0,0,0,0.4)"
              : "0 8px 24px rgba(0,0,0,0.12)",
            overflow: "visible",
            "&::before": {
              content: '""',
              display: "block",
              position: "absolute",
              top: 0,
              right: 60,
              width: 10,
              height: 10,
              bgcolor: theme.palette.background.paper,
              transform: "translateY(-50%) rotate(45deg)",
              zIndex: 0,
              borderLeft: `1px solid ${theme.palette.divider}`,
              borderTop: `1px solid ${theme.palette.divider}`,
            },
          },
        }}
      >
        <Box sx={{ px: 2.5, py: 2, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography
            variant="h6"
            sx={{ fontWeight: 600, color: theme.palette.text.primary, fontSize: 16 }}
          >
            Notifications
          </Typography>
          {unreadCount > 0 && (
            <Typography
              variant="caption"
              sx={{ color: theme.palette.primary.main, fontWeight: 600, cursor: "pointer" }}
              onClick={handleMarkAllRead}
            >
              Mark all read
            </Typography>
          )}
        </Box>
        <Divider sx={{ borderColor: theme.palette.divider }} />

        {notifications.length === 0 ? (
          <Box sx={{ px: 2.5, py: 3, textAlign: "center" }}>
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
              No notifications
            </Typography>
          </Box>
        ) : (
          notifications.slice(0, 10).map((n) => (
            <MenuItem
              key={n.id}
              onClick={() => { handleMarkRead(n.id); handleNotificationMenuClose(); }}
              sx={{
                mx: 1,
                my: 0.5,
                borderRadius: 1.5,
                py: 1.5,
                alignItems: "flex-start",
                bgcolor: n.status !== 'read'
                  ? (theme.palette.mode === 'dark' ? "rgba(80,118,171,0.12)" : "rgba(80,118,171,0.06)")
                  : "transparent",
                "&:hover": {
                  bgcolor: theme.palette.mode === 'dark'
                    ? theme.palette.action.hover
                    : "rgba(80,118,171,0.1)",
                },
                transition: "all 0.2s",
              }}
            >
              <Box>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: n.status !== 'read' ? 600 : 400, color: theme.palette.text.primary, mb: 0.5 }}
                >
                  {n.subject}
                </Typography>
                <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: 12 }}>
                  {n.message}
                </Typography>
                <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: 11, display: "block", mt: 0.25 }}>
                  {new Date(n.created_at).toLocaleString()}
                </Typography>
              </Box>
            </MenuItem>
          ))
        )}
      </Menu>
    </Toolbar>
  );
};

export default Header;
